/**
 * Unit card — relay controller / camera interface device identity panel on the
 * site dashboard rail.
 *
 * Reads device data from the new odl-app `unit` contract prop (plain JS,
 * sourced from the siteDashboard slice via the view). The legacy
 * getSelectedStructure selector is NOT used here.
 *
 * Contains:
 * - UnitCard — dashboard rail card (default export, connect-wrapped)
 */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import BasicDashboardCard from '@sophocles/components/BasicDashboardCard';
import { getCompletedUnitToSiteMapping } from '@sophocles/entities/CompletedUnitCommand';
import { selectCapabilities } from '@sophocles/features/capabilities';
import { withRouter } from '@sophocles/router/hoc';
import { determineIsODLEngineering } from '@sophocles/utils';
import ChangeUnitDialog from '@sophocles/views/sites/components/UnitCard/ChangeUnitDialog';
import UnitDetailsList from '@sophocles/views/sites/components/UnitCard/UnitDetailsList';

// TODO: Pretty sure this can be removed now that the site dashboard is using the new unit contract prop instead of the legacy getSelectedStructure selector. Leaving it for now until we can confirm that the site dashboard is fully migrated to the new contract prop.
// As part of that - we can see what else is orphaned and remove it. For example, the site dashboard is using the new unit contract prop instead of the legacy getSelectedStructure selector, so we can remove that selector and any related code.
class UnitCard extends React.Component {
  static defaultProps = {
    // Set to false on the site dashboard — the dashboard no longer uses the
    // RelayControllerStatus entity; online/last-heard come from the device record.
    shouldFetchStatus: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      changeUnitOpen: false,
      scanOnOpen: false,
    };
  }

  openChangeUnitDialog = (scanOnOpen = false) => {
    this.setState({ scanOnOpen, changeUnitOpen: true });
  };

  closeChangeUnitDialog = () => this.setState({ scanOnOpen: false, changeUnitOpen: false });

  render() {
    const { loading, unit, site, siteId, completedCommands, capabilities, params, isBillboard, t } = this.props;

    // capabilities slice for is_staff and can_manage_unit.
    const isStaff = capabilities?.is_staff ?? false;
    // can_manage_unit covers staff + reseller + at-least-site-group-admin.
    const canManageUnit = capabilities?.can_manage_unit ?? false;
    // isEngineering: derived from capabilities.groups (group id 1 = ODL Engineering).
    const isEngineering = determineIsODLEngineering(capabilities.groups ?? []);

    // Unit is present when the site has a provisioned relay controller.
    const hasUnit = unit != null;

    return (
      <BasicDashboardCard loading={loading}>
        {canManageUnit ? (
          <ChangeUnitDialog
            open={this.state.changeUnitOpen}
            edit={hasUnit}
            onClose={this.closeChangeUnitDialog}
            triggerScanOnOpen={this.state.scanOnOpen}
            editDevice={hasUnit}
            siteId={siteId ?? params?.site_id}
          />
        ) : null}

        {hasUnit ? (
          <UnitDetailsList
            isBillboard={isBillboard}
            isStaff={isStaff}
            isEngineering={isEngineering}
            canManageUnit={canManageUnit}
            unit={unit}
            site={site}
            siteId={siteId ?? params?.site_id}
            completedCommands={completedCommands}
            params={params}
            openSwapDialog={() => {
              this.openChangeUnitDialog(true);
            }}
            canChangeUnit={canManageUnit}
          />
        ) : (
          <Box
            sx={{
              minHeight: 180,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 'var(--space-3)',
              py: 'var(--space-4)',
            }}
          >
            <Button variant="text" disabled={!canManageUnit} onClick={() => this.openChangeUnitDialog(false)}>
              {t('sites.addDevice', 'Add Device')}
            </Button>
          </Box>
        )}
      </BasicDashboardCard>
    );
  }
}

function mapStateToProps(state) {
  return {
    capabilities: selectCapabilities(state),
    completedCommands: getCompletedUnitToSiteMapping(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {};
}

const UnitCardContainer = withRouter(connect(mapStateToProps, mapDispatchToProps)(UnitCard));

export default withTranslation()(UnitCardContainer);
